/**
 * Series for the QXO roofing market dashboard.
 * Only public prints. Gaps stay null. Derived rows are flagged.
 * As of 21 Sep 2026.
 */

export const ROOFING_DASHBOARD_AS_OF = 'As of 21 Sep 2026';

export type ArmaQuarter = {
  period: string;
  squaresM: number;
  yoy: number | null;
  /** Residual from ARMA year-end / Q3 YTD, not the Q4 PDF itself. */
  derived?: boolean;
};

/** U.S. asphalt shingles, million squares. Latest ARMA summary PDFs; Q4 23/24 derived from 2024 YE 172M. */
export const ARMA_QUARTERLY: ArmaQuarter[] = [
  { period: 'Q1 22', squaresM: 42.93, yoy: null },
  { period: 'Q2 22', squaresM: 45.52, yoy: null },
  { period: 'Q3 22', squaresM: 39.43, yoy: -6.2 },
  { period: 'Q4 22', squaresM: 29.87, yoy: -19.3 },
  { period: 'Q1 23', squaresM: 33.83, yoy: -21.2 },
  { period: 'Q2 23', squaresM: 51.71, yoy: 13.6 },
  { period: 'Q3 23', squaresM: 45.72, yoy: 15.9 },
  { period: 'Q4 23', squaresM: 37.37, yoy: 25.1, derived: true },
  { period: 'Q1 24', squaresM: 43.02, yoy: 27.2 },
  { period: 'Q2 24', squaresM: 46.76, yoy: -9.6 },
  { period: 'Q3 24', squaresM: 44.9, yoy: -1.8 },
  { period: 'Q4 24', squaresM: 37.32, yoy: -0.1, derived: true },
  { period: 'Q1 25', squaresM: 42.3, yoy: -1.7 },
  { period: 'Q2 25', squaresM: 44.74, yoy: -4.3 },
  { period: 'Q3 25', squaresM: 40.39, yoy: -10.0 },
  { period: 'Q1 26', squaresM: 38.13, yoy: -9.9 },
  { period: 'Q2 26', squaresM: 44.84, yoy: 0.2 },
];

export const ARMA_LATEST = {
  q2Squares: 44_838_320,
  q2Yoy: 0.2,
  h1Yoy: -4.7,
  q3Guide: 'OC: ARMA Q3 2026 −HSD after June price pull-forward (not a print)',
} as const;

/**
 * Overlay vs mill YoY. QXO SSS is Craig Webb 17 Aug 2026 citing a company official — not in the 10-Q.
 * Beacon organic includes greenfields (Q2 2024 earnings).
 */
export const MILL_VS_BRANCH = ARMA_QUARTERLY.filter((row) => row.yoy != null).map((row) => ({
  period: row.period,
  armaYoy: row.yoy as number,
  beaconOrganic: row.period === 'Q2 24' ? 2.8 : null,
  qxoSteepSss: row.period === 'Q2 26' ? -5.8 : null,
  qxoAllSss: row.period === 'Q2 26' ? -1.6 : null,
}));

/** Owens Corning Roofing net sales $ millions and explicit volume YoY when disclosed. */
export const OC_ROOFING = [
  { period: 'Q1 25', salesM: 1120, salesYoy: null as number | null, volYoy: null as number | null },
  { period: 'Q2 25', salesM: 1303, salesYoy: 4, volYoy: null },
  { period: 'Q3 25', salesM: 1200, salesYoy: 2, volYoy: null },
  { period: 'Q4 25', salesM: 774, salesYoy: -27, volYoy: null },
  { period: 'Q1 26', salesM: 960, salesYoy: -14, volYoy: null },
  { period: 'Q2 26', salesM: 1313, salesYoy: 1, volYoy: -1 },
];

export const OC_CHANNEL = {
  h1VolYoy: -7,
  q2VolYoy: -1,
  q3ArmaGuide: '−HSD',
  regionsGood: 'Midwest, Upper Midwest, Mid-Atlantic',
  regionsWeak: 'Southwest, Southeast (storm-dependent)',
  inventory: 'Distributor inventories a little heavier than normal, very regional (OC Q2 2026 call)',
  stormCarryover: '0 storm-carryover squares into 2026 vs ~3M in 1H 2025',
} as const;

/** Quarterly average of monthly SAAR, Census/HUD via FRED HOUST / HOUST1F. Q3 26 = Jul–Aug only. */
export const HOUSING_STARTS_Q = [
  { period: 'Q1 19', total: 1.179, single: 0.844 },
  { period: 'Q2 19', total: 1.269, single: 0.866 },
  { period: 'Q3 19', total: 1.309, single: 0.908 },
  { period: 'Q4 19', total: 1.409, single: 0.937 },
  { period: 'Q1 20', total: 1.465, single: 0.961 },
  { period: 'Q2 20', total: 1.08, single: 0.777 },
  { period: 'Q3 20', total: 1.465, single: 1.054 },
  { period: 'Q4 20', total: 1.567, single: 1.21 },
  { period: 'Q1 21', total: 1.57, single: 1.128 },
  { period: 'Q2 21', total: 1.584, single: 1.109 },
  { period: 'Q3 21', total: 1.591, single: 1.117 },
  { period: 'Q4 21', total: 1.668, single: 1.164 },
  { period: 'Q1 22', total: 1.708, single: 1.177 },
  { period: 'Q2 22', total: 1.636, single: 1.089 },
  { period: 'Q3 22', total: 1.465, single: 0.91 },
  { period: 'Q4 22', total: 1.392, single: 0.839 },
  { period: 'Q1 23', total: 1.373, single: 0.826 },
  { period: 'Q2 23', total: 1.457, single: 0.937 },
  { period: 'Q3 23', total: 1.385, single: 0.981 },
  { period: 'Q4 23', total: 1.469, single: 1.045 },
  { period: 'Q1 24', total: 1.41, single: 1.062 },
  { period: 'Q2 24', total: 1.342, single: 1.008 },
  { period: 'Q3 24', total: 1.34, single: 0.982 },
  { period: 'Q4 24', total: 1.388, single: 1.008 },
  { period: 'Q1 25', total: 1.397, single: 1.012 },
  { period: 'Q2 25', total: 1.356, single: 0.941 },
  { period: 'Q3 25', total: 1.347, single: 0.89 },
  { period: 'Q4 25', total: 1.323, single: 0.923 },
  { period: 'Q1 26', total: 1.418, single: 0.945 },
  { period: 'Q2 26', total: 1.345, single: 0.902 },
  { period: 'Q3 26*', total: 1.292, single: 0.886 },
];

export const HOUSING_LATEST = {
  month: 'Aug 2026',
  totalSaar: 1.275,
  singleSaar: 0.918,
  yoyTotal: -1.2,
  note: 'FRED HOUST 21 Sep 2026 pull. Aug 2025 was 1.291M.',
} as const;

/** Month-end 30-year FRM, Freddie Mac via FRED MORTGAGE30US, then quarterly average. */
export const MORTGAGE_Q = [
  { period: 'Q1 19', rate: 4.29 },
  { period: 'Q2 19', rate: 3.97 },
  { period: 'Q3 19', rate: 3.66 },
  { period: 'Q4 19', rate: 3.73 },
  { period: 'Q1 20', rate: 3.49 },
  { period: 'Q2 20', rate: 3.17 },
  { period: 'Q3 20', rate: 2.93 },
  { period: 'Q4 20', rate: 2.73 },
  { period: 'Q1 21', rate: 2.96 },
  { period: 'Q2 21', rate: 2.98 },
  { period: 'Q3 21', rate: 2.89 },
  { period: 'Q4 21', rate: 3.12 },
  { period: 'Q1 22', rate: 4.04 },
  { period: 'Q2 22', rate: 5.3 },
  { period: 'Q3 22', rate: 5.85 },
  { period: 'Q4 22', rate: 6.69 },
  { period: 'Q1 23', rate: 6.32 },
  { period: 'Q2 23', rate: 6.57 },
  { period: 'Q3 23', rate: 7.1 },
  { period: 'Q4 23', rate: 7.21 },
  { period: 'Q1 24', rate: 6.81 },
  { period: 'Q2 24', rate: 7.02 },
  { period: 'Q3 24', rate: 6.4 },
  { period: 'Q4 24', rate: 6.79 },
  { period: 'Q1 25', rate: 6.79 },
  { period: 'Q2 25', rate: 6.82 },
  { period: 'Q3 25', rate: 6.53 },
  { period: 'Q4 25', rate: 6.18 },
  { period: 'Q1 26', rate: 6.15 },
  { period: 'Q2 26', rate: 6.44 },
  { period: 'Q3 26', rate: 6.76 },
];

export const MORTGAGE_LATEST = { date: '17 Sep 2026', rate: 6.95 } as const;

/** NAR existing-home sales, millions. Annual totals; Aug 2026 is SAAR. */
export const EXISTING_HOME_SALES_ANNUAL = [
  { year: '2018', salesM: 5.34 },
  { year: '2019', salesM: 5.34 },
  { year: '2020', salesM: 5.64 },
  { year: '2021', salesM: 6.12 },
  { year: '2022', salesM: 5.03 },
  { year: '2023', salesM: 4.09 },
  { year: '2024', salesM: 4.06 },
  { year: '2025', salesM: 4.06 },
];

export const EXISTING_HOME_SALES = {
  month: 'Aug 2026',
  saarM: 3.98,
  yoy: -1.2,
  mom: -2.0,
  ytd: 1.6,
  inventoryM: 1.62,
  monthsSupply: 4.9,
} as const;

/** IBP installation residential same-branch YoY. Filings. */
export const IBP_RESI_SB = [
  { period: 'Q1 25', yoy: -4.6 },
  { period: 'Q2 25', yoy: -1.1 },
  { period: 'Q3 25', yoy: -2.8 },
  { period: 'Q4 25', yoy: -9.3 },
  { period: 'Q1 26', yoy: -11.2 },
  { period: 'Q2 26', yoy: -6.1 },
];

export const IBP_CONSOL_SB = [
  { period: 'Q1 25', yoy: -4.2 },
  { period: 'Q2 25', yoy: 0.7 },
  { period: 'Q3 25', yoy: 0.4 },
  { period: 'Q4 25', yoy: -2.4 },
  { period: 'Q1 26', yoy: -5.9 },
  { period: 'Q2 26', yoy: -0.6 },
];

/** BLDR core organic (ex commodities, acquisitions, selling days). Sparse — only verified quarters. */
export const BLDR_CORE_ORGANIC = [
  { period: 'Q4 25', yoy: -14.0 },
  { period: 'Q2 26', yoy: -7.0, sf: -8.1, mf: -9.7, rr: -1.8 },
];

export const ABC_SUPPLY = {
  fy2024: '~$20.4–20.7B (Forbes / MDM; private, not a 10-K)',
  fy2025: '$20.2B, −2.3% (trade press citing company)',
} as const;

export const HAIL_SPC_ONE_INCH = [
  { year: '2024', events: 5373 },
  { year: '2025', events: 5432 },
];

export const QXO_Q2_SSS = {
  all: -1.6,
  steep: -5.8,
  steepVolume: -5.3,
  steepPrice: -0.5,
  lowSlope: -1.3,
  complementary: 6.4,
  source: 'Craig Webb, 17 Aug 2026, citing a QXO official. Not in the Q2 10-Q.',
} as const;

export const KPIS = [
  {
    id: 'arma',
    label: 'Mill shingles, Q2 26',
    value: '+0.2%',
    hint: '44.8M squares · first half −4.7%',
    tone: 'neutral' as const,
  },
  {
    id: 'sss',
    label: 'QXO steep-slope stores, Q2 26',
    value: '−5.8%',
    hint: 'Same-store, not in 10-Q · volume −5.3, price −0.5',
    tone: 'danger' as const,
  },
  {
    id: 'starts',
    label: 'Homes started, Aug 26',
    value: '1.275M',
    hint: 'Annualized · −1.2% vs last year · houses 918k',
    tone: 'warning' as const,
  },
  {
    id: 'mort',
    label: '30-year mortgage',
    value: '6.95%',
    hint: 'Freddie Mac, 17 Sep 2026',
    tone: 'warning' as const,
  },
  {
    id: 'ehs',
    label: 'Existing homes sold, Aug 26',
    value: '3.98M',
    hint: 'Annualized · −1.2% vs last year · 4.9 months of supply',
    tone: 'neutral' as const,
  },
  {
    id: 'oc',
    label: 'Owens Corning roofing volume, Q2 26',
    value: '−1%',
    hint: 'First half −7% · they guided Q3 mill shipments down high-single-digits',
    tone: 'warning' as const,
  },
];

export const WATCHLIST = [
  {
    item: 'Q3 mill shingle shipments',
    why: 'Owens Corning said Q2 was pulled forward by a June price hike. If mills fall high-single-digits, QXO is either destocking or losing sell-through.',
    next: 'Late October (ARMA PDF)',
  },
  {
    item: 'QXO’s first quarter with TopBuild',
    why: 'Q2 still excludes the installer. Ask for Beacon-only same-store, not a mix that jumps because Kodiak lumber is in the print.',
    next: 'November earnings',
  },
  {
    item: 'Owens Corning roofing volume vs mills',
    why: 'Shows whether the branded mill kept share after the June buy-in.',
    next: 'OC Q3 call',
  },
  {
    item: 'Homes completed vs installer same-branch',
    why: 'Completions lag starts and hit jobsites later. August completions were −27% vs last year — that is the print to map onto Q3 install.',
    next: 'Census; IBP and QXO Q3',
  },
  {
    item: 'Asphalt cost vs shingle selling prices',
    why: 'Feedstock is up ~15% vs last year; mill shingle prices are up ~5%. QXO only feels that if it cannot pass freight and product cost through.',
    next: 'BLS monthly; OC Q3 price/cost',
  },
  {
    item: 'New-home inventory vs house starts',
    why: 'Builders are sitting on 9.6 months of supply. Starts can look “up” vs a weak year-ago month and still not restock distributors.',
    next: 'Census new-home sales',
  },
  {
    item: 'Diesel vs QXO delivery commentary',
    why: 'Diesel is a branch cost, not a mill spread. August was +78% vs last year — watch for freight surcharges or gross-margin mix.',
    next: 'BLS monthly; QXO Q3',
  },
  {
    item: 'Harvard remodeling outlook',
    why: 'The July model has owner-occupied repair/remodel slowing to +0.5% by mid-2027 on a $519B run-rate. That is reroof dollars, not mill squares.',
    next: '22 Oct 2026',
  },
];

export const DATA_QUALITY = [
  {
    series: 'Mill shipments (ARMA)',
    grade: 'Primary',
    note: 'Squares the factories shipped, including restock into distributor yards. Q4 2023–24 are inferred from the 172M 2024 year-end total. Q4 2025 is still missing.',
  },
  {
    series: 'Census housing and BLS prices',
    grade: 'Primary',
    note: 'Permits, starts, completions, and new-home sales are annualized monthly rates, averaged into quarters. Price indexes are rebased so 2019 Q1 = 100 — they show change, not dollar prices. Housing “stocks” (under construction, not started) are a count of homes, not an annualized flow.',
  },
  {
    series: 'Company organic / same-store',
    grade: 'Primary',
    note: 'Each company defines its own split (IBP same-branch, TopBuild same-branch, BLDR core organic). Do not average them into one “organic” number.',
  },
  {
    series: 'QXO same-store and sales mix',
    grade: 'Secondary',
    note: 'Same-store is a trade-press quote of a QXO official, not the 10-Q. The mix chart is reported sales: Q2 2026 complementary jumps because Kodiak lumber entered the print.',
  },
  {
    series: 'Architecture billings, hail counts, Harvard remodeling model',
    grade: 'Context only',
    note: 'Useful color, not volume. Below 50 on architecture billings means design firms are contracting. Hail events are not squares of shingles. The Harvard remodeling figure is a forecast, not a Census print.',
  },
];
